import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { ChangeEvent, memo } from 'react';

import { Country } from 'shared/api';

type Props = {
  filter: string;
  byCountry: string;
  countries: Country[];
  totalCount: number;
  onFilterChange: (value: string) => void;
  onCountryChange: (code: string) => void;
  addButton?: React.ReactNode;
};

export const PharmaCompaniesFilters = memo(
  ({ filter, byCountry, countries, totalCount, onFilterChange, onCountryChange, addButton }: Props) => {
    const selectedCountry = countries.find((c) => c.code === byCountry) ?? null;

    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e.target.value);
    };

    const handleClearFilter = () => {
      onFilterChange('');
    };

    return (
      <Paper sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" p="8px 16px" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
            Pharmaceutical companies
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flex={1} minWidth="300px">
            <TextField
              sx={{ minWidth: '350px', maxWidth: '500px', flex: 1 }}
              placeholder="Search by name, abbreviation, russian name..."
              size="small"
              value={filter}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton size="small" onClick={handleClearFilter}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />

            <Autocomplete
              size="small"
              sx={{ minWidth: 220 }}
              options={countries}
              value={selectedCountry}
              onChange={(_, value) => onCountryChange(value?.code ?? '')}
              getOptionLabel={(option) => `${option.name} (${option.code})`}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderInput={(params) => <TextField {...params} label="Country" placeholder="All countries" />}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
            {totalCount} {totalCount === 1 ? 'company' : 'companies'}
          </Typography>

          {addButton && <Box>{addButton}</Box>}
        </Box>
      </Paper>
    );
  },
);

PharmaCompaniesFilters.displayName = 'PharmaCompaniesFilters';
